// Holds the Supabase session + the user's profile (role, reseller_name).
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, IDLE_TIMEOUT_MINUTES } from '../lib/supabaseClient';
import { useIdleLogout } from '../hooks/useIdleLogout';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // True when the server says this session still needs the emailed 2FA code.
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) setProfile(null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Load profile whenever a session appears. If the server answers 'mfa_required',
  // the user must enter the emailed code before anything else loads.
  useEffect(() => {
    if (!session) { setMfaRequired(false); return; }
    api.get('/api/auth/me')
      .then((p) => { setProfile(p); setMfaRequired(false); })
      .catch((err) => {
        setProfile(null);
        if (err.code === 'mfa_required') setMfaRequired(true);
      });
  }, [session]);

  // Re-fetch the profile (after the user edits their own details).
  const refreshProfile = async () => {
    const p = await api.get('/api/auth/me');
    setProfile(p);
    return p;
  };

  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  };

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setMfaRequired(false);
  }, []);

  // Inactivity sign-out. Applies from the moment the password is accepted, so a
  // 2FA screen left open is closed out too.
  const lastActivityRef = useIdleLogout(!!session, IDLE_TIMEOUT_MINUTES, logout);

  // Heartbeat. The server expires a 2FA-verified session that hasn't been used for
  // the same idle window, and the dashboard can go a long time between requests
  // (reading a page it already loaded). Without this, someone actively working
  // would get a surprise code prompt; with it, only real inactivity expires.
  const lastBeatRef = useRef(Date.now());
  useEffect(() => {
    if (!session || mfaRequired) return undefined;
    const everyMs = Math.max(60, (IDLE_TIMEOUT_MINUTES * 60) / 3) * 1000;
    const beat = () => {
      // Only if the user did something since the last beat — an abandoned tab
      // must be allowed to go stale.
      if (lastActivityRef.current < lastBeatRef.current) return;
      lastBeatRef.current = Date.now();
      api.get('/api/auth/me').catch(() => { /* handled by the normal error paths */ });
    };
    const id = setInterval(beat, everyMs);
    return () => clearInterval(id);
  }, [session, mfaRequired, lastActivityRef]);

  // Authenticator-app 2FA: is it set up yet, and if not, start the QR enrollment.
  const getMfaStatus = () => api.get('/api/auth/mfa/status');
  const enrollMfa = () => api.post('/api/auth/mfa/enroll');

  // Submit the app's 6-digit code; on success the session is fully unlocked.
  const verifyMfa = async (code) => {
    await api.post('/api/auth/mfa/verify', { code });
    setMfaRequired(false);
    const p = await api.get('/api/auth/me');
    setProfile(p);
  };

  // Send a password-reset email; the link returns the user to /reset-password.
  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  };

  // Set a new password for the current (recovery) session.
  const updatePassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(error.message);
  };

  return (
    <AuthContext.Provider value={{ session, profile, loading, mfaRequired, getMfaStatus, enrollMfa, verifyMfa, login, logout, sendPasswordReset, updatePassword, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
